/** Synth presets — predefined oscillator layer configurations.
 *
 * Each preset defines a name, oscillator layers, delay settings, and
 * optionally any other SynthParams overrides. Users can load presets
 * to quickly configure their synth for common sound types.
 */

import type { DelayParams, OscillatorLayer } from "../types/synth.ts";
import { DEFAULT_DELAY, DEFAULT_LAYER } from "../types/synth.ts";
import type { ArpParams } from "./Arpeggiator.ts";

/** A synth preset definition. */
export type SynthPreset = {
	readonly id: string;
	readonly name: string;
	readonly category: "bass" | "pad" | "lead" | "keys" | "custom";
	readonly description: string;
	readonly layers: readonly OscillatorLayer[];
	readonly delay: DelayParams;
	/** Optional arpeggiator settings included with the preset. */
	readonly arp?: ArpParams;
};

/** Built-in synth presets. */
export const BUILT_IN_PRESETS: readonly SynthPreset[] = [
	{
		id: "preset-init",
		name: "Init",
		category: "keys",
		description: "Default single-layer sawtooth.",
		layers: [DEFAULT_LAYER],
		delay: DEFAULT_DELAY,
	},
	{
		id: "preset-bass-sub",
		name: "Sub Bass",
		category: "bass",
		description: "Deep sub bass with sine wave and tight envelope.",
		layers: [
			{
				id: "layer-1",
				waveform: "sine",
				semitoneOffset: -12,
				centOffset: 0,
				gain: 1.0,
				adsr: { attack: 0.005, decay: 0.15, sustain: 0.8, release: 0.1 },
				filter: { cutoff: 400, resonance: 1.0 },
				pan: 0,
				enabled: true,
			},
		],
		delay: DEFAULT_DELAY,
	},
	{
		id: "preset-bass-growl",
		name: "Growl Bass",
		category: "bass",
		description: "Aggressive bass with layered saw and square.",
		layers: [
			{
				id: "layer-1",
				waveform: "sawtooth",
				semitoneOffset: -12,
				centOffset: 0,
				gain: 0.8,
				adsr: { attack: 0.005, decay: 0.2, sustain: 0.6, release: 0.15 },
				filter: { cutoff: 800, resonance: 4.0 },
				pan: 0,
				enabled: true,
			},
			{
				id: "layer-2",
				waveform: "square",
				semitoneOffset: -12,
				centOffset: 5,
				gain: 0.5,
				adsr: { attack: 0.005, decay: 0.25, sustain: 0.5, release: 0.15 },
				filter: { cutoff: 600, resonance: 2.0 },
				pan: 0,
				enabled: true,
			},
		],
		delay: DEFAULT_DELAY,
	},
	{
		id: "preset-pad-warm",
		name: "Warm Pad",
		category: "pad",
		description: "Lush warm pad with slow attack and detuned layers.",
		layers: [
			{
				id: "layer-1",
				waveform: "sawtooth",
				semitoneOffset: 0,
				centOffset: -7,
				gain: 0.6,
				adsr: { attack: 0.8, decay: 0.5, sustain: 0.7, release: 1.5 },
				filter: { cutoff: 3000, resonance: 1.0 },
				pan: 0,
				enabled: true,
			},
			{
				id: "layer-2",
				waveform: "sawtooth",
				semitoneOffset: 0,
				centOffset: 7,
				gain: 0.6,
				adsr: { attack: 0.8, decay: 0.5, sustain: 0.7, release: 1.5 },
				filter: { cutoff: 3000, resonance: 1.0 },
				pan: 0,
				enabled: true,
			},
			{
				id: "layer-3",
				waveform: "triangle",
				semitoneOffset: 12,
				centOffset: 0,
				gain: 0.3,
				adsr: { attack: 1.2, decay: 0.8, sustain: 0.5, release: 2.0 },
				filter: { cutoff: 2000, resonance: 0.5 },
				pan: 0,
				enabled: true,
			},
		],
		delay: {
			...DEFAULT_DELAY,
			enabled: true,
			feedback: 0.25,
			mix: 0.2,
			timeMode: "sync",
			noteDivision: "1/4",
		},
	},
	{
		id: "preset-pad-strings",
		name: "String Pad",
		category: "pad",
		description: "Ethereal string-like pad with wide stereo spread.",
		layers: [
			{
				id: "layer-1",
				waveform: "sawtooth",
				semitoneOffset: 0,
				centOffset: -12,
				gain: 0.5,
				adsr: { attack: 1.0, decay: 0.6, sustain: 0.65, release: 2.0 },
				filter: { cutoff: 4000, resonance: 0.8 },
				pan: 0,
				enabled: true,
			},
			{
				id: "layer-2",
				waveform: "sawtooth",
				semitoneOffset: 0,
				centOffset: 12,
				gain: 0.5,
				adsr: { attack: 1.0, decay: 0.6, sustain: 0.65, release: 2.0 },
				filter: { cutoff: 4000, resonance: 0.8 },
				pan: 0,
				enabled: true,
			},
			{
				id: "layer-3",
				waveform: "sawtooth",
				semitoneOffset: 12,
				centOffset: -5,
				gain: 0.25,
				adsr: { attack: 1.5, decay: 0.8, sustain: 0.5, release: 2.5 },
				filter: { cutoff: 2500, resonance: 1.0 },
				pan: 0,
				enabled: true,
			},
			{
				id: "layer-4",
				waveform: "sawtooth",
				semitoneOffset: 12,
				centOffset: 5,
				gain: 0.25,
				adsr: { attack: 1.5, decay: 0.8, sustain: 0.5, release: 2.5 },
				filter: { cutoff: 2500, resonance: 1.0 },
				pan: 0,
				enabled: true,
			},
		],
		delay: {
			...DEFAULT_DELAY,
			enabled: true,
			feedback: 0.3,
			mix: 0.25,
			timeMode: "sync",
			noteDivision: "1/8",
			pingPong: true,
		},
	},
	{
		id: "preset-lead-mono",
		name: "Mono Lead",
		category: "lead",
		description: "Classic mono synth lead with square wave bite.",
		layers: [
			{
				id: "layer-1",
				waveform: "square",
				semitoneOffset: 0,
				centOffset: 0,
				gain: 0.7,
				adsr: { attack: 0.005, decay: 0.15, sustain: 0.6, release: 0.2 },
				filter: { cutoff: 5000, resonance: 3.0 },
				pan: 0,
				enabled: true,
			},
			{
				id: "layer-2",
				waveform: "sawtooth",
				semitoneOffset: 0,
				centOffset: 8,
				gain: 0.5,
				adsr: { attack: 0.005, decay: 0.15, sustain: 0.6, release: 0.2 },
				filter: { cutoff: 5000, resonance: 2.0 },
				pan: 0,
				enabled: true,
			},
		],
		delay: {
			...DEFAULT_DELAY,
			enabled: true,
			feedback: 0.3,
			mix: 0.2,
			timeMode: "sync",
			noteDivision: "1/8d",
		},
	},
	{
		id: "preset-lead-supersaw",
		name: "Supersaw Lead",
		category: "lead",
		description: "Fat supersaw lead with multiple detuned oscillators.",
		layers: [
			{
				id: "layer-1",
				waveform: "sawtooth",
				semitoneOffset: 0,
				centOffset: 0,
				gain: 0.7,
				adsr: { attack: 0.01, decay: 0.2, sustain: 0.7, release: 0.3 },
				filter: { cutoff: 8000, resonance: 1.5 },
				pan: 0,
				enabled: true,
			},
			{
				id: "layer-2",
				waveform: "sawtooth",
				semitoneOffset: 0,
				centOffset: -10,
				gain: 0.5,
				adsr: { attack: 0.01, decay: 0.2, sustain: 0.7, release: 0.3 },
				filter: { cutoff: 8000, resonance: 1.5 },
				pan: 0,
				enabled: true,
			},
			{
				id: "layer-3",
				waveform: "sawtooth",
				semitoneOffset: 0,
				centOffset: 10,
				gain: 0.5,
				adsr: { attack: 0.01, decay: 0.2, sustain: 0.7, release: 0.3 },
				filter: { cutoff: 8000, resonance: 1.5 },
				pan: 0,
				enabled: true,
			},
			{
				id: "layer-4",
				waveform: "sawtooth",
				semitoneOffset: 12,
				centOffset: 0,
				gain: 0.3,
				adsr: { attack: 0.01, decay: 0.2, sustain: 0.5, release: 0.3 },
				filter: { cutoff: 6000, resonance: 1.0 },
				pan: 0,
				enabled: true,
			},
		],
		delay: {
			...DEFAULT_DELAY,
			enabled: true,
			feedback: 0.25,
			mix: 0.15,
			timeMode: "sync",
			noteDivision: "1/8",
		},
	},
	{
		id: "preset-keys-electric",
		name: "Electric Piano",
		category: "keys",
		description: "Electric piano with sine and triangle layers.",
		layers: [
			{
				id: "layer-1",
				waveform: "sine",
				semitoneOffset: 0,
				centOffset: 0,
				gain: 0.8,
				adsr: { attack: 0.005, decay: 0.4, sustain: 0.4, release: 0.5 },
				filter: { cutoff: 6000, resonance: 0.5 },
				pan: 0,
				enabled: true,
			},
			{
				id: "layer-2",
				waveform: "triangle",
				semitoneOffset: 12,
				centOffset: 0,
				gain: 0.3,
				adsr: { attack: 0.005, decay: 0.2, sustain: 0.1, release: 0.3 },
				filter: { cutoff: 4000, resonance: 1.0 },
				pan: 0,
				enabled: true,
			},
		],
		delay: {
			...DEFAULT_DELAY,
			enabled: true,
			feedback: 0.2,
			mix: 0.15,
			timeMode: "sync",
			noteDivision: "1/4",
		},
	},
] as const;

/** Get a preset by ID. */
export function getPresetById(id: string): SynthPreset | undefined {
	return BUILT_IN_PRESETS.find((p) => p.id === id);
}

/** Get all presets in a specific category. */
export function getPresetsByCategory(
	category: SynthPreset["category"],
): readonly SynthPreset[] {
	return BUILT_IN_PRESETS.filter((p) => p.category === category);
}
